import { CartUpdatedEvent } from '@theme/events';
import { formatMoney } from '@theme/utilities';

class FrequentlyBoughtTogether extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.items = this.querySelectorAll('[data-fbt-item]');
    this.submitButton = this.querySelector('[data-fbt-submit]');
    this.buttonPrice = this.querySelector('[data-fbt-button-price]');
    this.buttonCompare = this.querySelector('[data-fbt-button-compare]');
    this.buttonLabel = this.dataset.buttonLabel || 'Add to cart';

    for (const checkbox of this.querySelectorAll('[data-fbt-checkbox]')) {
      checkbox.addEventListener('change', this.#handleCheckboxChange);
    }

    this.form?.addEventListener('submit', this.#handleSubmit);
    this.#updateTotals();
  }

  disconnectedCallback() {
    for (const checkbox of this.querySelectorAll('[data-fbt-checkbox]')) {
      checkbox.removeEventListener('change', this.#handleCheckboxChange);
    }

    this.form?.removeEventListener('submit', this.#handleSubmit);
  }

  #handleCheckboxChange = (event) => {
    const item = event.target.closest('[data-fbt-item]');
    if (!item) return;

    item.classList.toggle('is-checked', event.target.checked);
    this.#updateTotals();
  };

  #getCheckedItems() {
    return Array.from(this.items).filter((item) => {
      const checkbox = item.querySelector('[data-fbt-checkbox]');
      return checkbox?.checked;
    });
  }

  #updateTotals() {
    let totalPrice = 0;
    let totalCompare = 0;

    for (const item of this.#getCheckedItems()) {
      const variantInput = item.querySelector('[data-fbt-variant-id]');
      if (!variantInput) continue;

      totalPrice += parseInt(variantInput.dataset.fbtPrice, 10) || 0;
      totalCompare += parseInt(variantInput.dataset.fbtComparePrice, 10) || 0;
    }

    if (this.buttonPrice) {
      this.buttonPrice.textContent = formatMoney(totalPrice);
    }

    const hasSavings = totalCompare > totalPrice;

    if (this.buttonCompare) {
      this.buttonCompare.textContent = formatMoney(totalCompare);
      this.buttonCompare.hidden = !hasSavings;
    }

    if (this.submitButton) {
      this.submitButton.disabled = this.#getCheckedItems().length === 0;
    }
  }

  #handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const checkedItems = this.#getCheckedItems();
    if (checkedItems.length === 0) return;

    this.submitButton?.classList.add('is-loading');

    const items = [];

    for (const item of checkedItems) {
      const variantInput = item.querySelector('[data-fbt-variant-id]');
      const quantityInput = item.querySelector('[data-fbt-quantity]');
      if (!variantInput?.value) continue;

      items.push({
        id: variantInput.value,
        quantity: parseInt(quantityInput?.value || '1', 10),
      });
    }

    const sections = [];
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer?.dataset.sectionId) {
      sections.push(cartDrawer.dataset.sectionId);
    }

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items,
          sections: sections.join(','),
        }),
      });

      const data = await response.json();
      this.#updateCart(data, items.length);
    } catch (error) {
      console.error(error);
    } finally {
      this.submitButton?.classList.remove('is-loading');
    }
  };

  #updateCart(data, itemsAdded) {
    if (window.location.pathname.includes('/cart')) {
      window.location.reload();
      return;
    }

    const currentItemCount = document.querySelector('cart-icon')?.getCurrentItemCount();
    const newItemCount = currentItemCount != null ? currentItemCount + itemsAdded : undefined;

    document.dispatchEvent(
      new CartUpdatedEvent(
        {},
        {
          itemCount: newItemCount,
          source: 'frequently-bought-together',
          sections: data.sections,
        },
      ),
    );
  }
}

if (!customElements.get('frequently-bought-together')) {
  customElements.define('frequently-bought-together', FrequentlyBoughtTogether);
}
